<template>
  <div class="department-tree">
    <div v-if="showFilter"
         class="department-tree__filter">
      <el-input v-model="filterText"
                placeholder="请输入部门或账号名称进行过滤"
                clearable
                prefix-icon="el-icon-search">
      </el-input>
    </div>

    <el-tree ref="tree"
             class="department-tree__body"
             :data="treeData"
             :props="treeProps"
             :node-key="nodeKey"
             :default-expand-all="defaultExpandAll"
             :default-checked-keys="defaultCheckedKeys"
             :default-expanded-keys="defaultExpandedKeys"
             :show-checkbox="showCheckbox"
             :check-strictly="checkStrictly"
             :expand-on-click-node="false"
             :filter-node-method="filterNode"
             highlight-current
             @node-click="onNodeClick"
             @check="onCheck">
      <span class="department-tree__node"
            slot-scope="{ node, data }">
        <i :class="data.type === 'user'
            ? 'el-icon-user-solid department-tree__icon--user'
            : 'el-icon-folder department-tree__icon--dept'"></i>
        <span class="department-tree__label">{{ data.name }}</span>
        <span v-if="showTreeUserid && data.type === 'user'"
              class="department-tree__userid">
              {{ data.userid }}
        </span>
      </span>
    </el-tree>
  </div>
</template>

<script>
export default {
  name: 'DepartmentTree',
  props: {
    // 是否显示过滤框
    showFilter: {
      type: Boolean,
      default: true,
    },
    // 默认是否展开全部
    defaultExpandAll: {
      type: Boolean,
      default: false,
    },
    // 是否显示勾选框（用于选择部门/参会人）
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    // 父子节点是否严格互不关联（勾部门不会连带勾选其下账号）
    checkStrictly: {
      type: Boolean,
      default: false,
    },
    // 默认勾选的节点 key 列表
    defaultCheckedKeys: {
      type: Array,
      default: () => [],
    },
    // 默认展开的节点 key 列表
    defaultExpandedKeys: {
      type: Array,
      default: () => [],
    },
    // 是否在数据树中显示 userid 数据
    showTreeUserid: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      filterText: '',
      loading: false,
      departments: [],
      users: [],
      userDeptMap: [],
      // el-tree 的字段映射
      treeProps: {
        label: 'name',
        children: 'children',
      },
      nodeKey: 'key',
    }
  },
  computed: {
    treeData () {
      return this.buildTree(this.departments, this.users, this.userDeptMap)
    },
  },
  created () {
    this.fetchData()
  },
  watch: {
    filterText (val) {
      this.$refs.tree && this.$refs.tree.filter(val)
    },
  },
  methods: {
    // 获取数据
    async fetchData () {
      this.loading = true
      try {
        const res = await request.common('getAllWecomDepartmentsApi')
        this.departments = res.data
        const res2 = await request.common('getAllActiveUsersApi')
        this.users = res2.data.users
        this.userDeptMap = res2.data.user_dept_map
      } finally {
        this.loading = false
      }
    },
    buildTree (departments = [], users = [], userDeptMap = []) {
      // 1. 用户字典：userid -> user
      const userMap = {}
      users.forEach(u => { userMap[u.userid] = u })

      // 2. 部门节点字典：id -> node（预置 children）
      const deptMap = {}
      departments.forEach(d => {
        deptMap[d.id] = {
          ...d,
          key: d.id,
          type: 'dept',
          children: [],
        }
      })

      // 3. 把账号挂到对应部门下
      const seenUserKeys = new Set()
      userDeptMap.forEach(m => {
        const deptNode = deptMap[m.wecom_department_id]
        const user = userMap[m.wecom_userid]
        if (deptNode && user) {
          const key = 'u_' + m.wecom_department_id + '_' + user.userid
          if (seenUserKeys.has(key)) return // 去重：防止同 (部门, userid) 映射重复出现导致 key 冲突
          seenUserKeys.add(key)
          deptNode.children.push({
            key,
            userid: user.userid,
            name: user.name,
            type: 'user',
          })
        }
      })

      // 4. 按 parentid 组装父子层级，未找到父级的当作根
      const roots = []
      departments.forEach(d => {
        const node = deptMap[d.id]
        const parent = deptMap[d.parentid]
        if (parent) {
          parent.children.push(node)
        } else {
          roots.push(node)
        }
      })
      return roots
    },
    filterNode (value, data) {
      if (!value) return true
      const keyword = String(value).toLowerCase()
      if (data.name && data.name.toLowerCase().indexOf(keyword) !== -1) return true
      // if (data.userid && data.userid.toLowerCase().indexOf(keyword) !== -1) return true
      return false
    },
    onNodeClick (data, node, component) {
      this.$emit('node-click', { data, node, component })
    },
    onCheck (data, checkedState) {
      this.$emit('check', { data, checkedKeys: checkedState.checkedKeys, checkedNodes: checkedState.checkedNodes })
    },
    // 暴露给父组件：获取 el-tree 实例
    getTreeRef () {
      return this.$refs.tree
    },
    // 获取勾选的 key 列表
    getCheckedKeys () {
      return this.$refs.tree ? this.$refs.tree.getCheckedKeys() : []
    },
    // 获取勾选的节点（含半选父节点）
    getCheckedNodes () {
      return this.$refs.tree ? this.$refs.tree.getCheckedNodes() : []
    },
    // 设置勾选的 key 列表
    setCheckedKeys (keys) {
      this.$refs.tree && this.$refs.tree.setCheckedKeys(keys || [])
    },
  },
}
</script>

<style scoped lang="scss">
.department-tree {
  &__filter {
    margin-bottom: 12px;
  }
  &__node {
    display: flex;
    align-items: center;
    width: 100%;
  }
  &__icon--dept {
    color: #e6a23c;
    margin-right: 6px;
  }
  &__icon--user {
    color: #409eff;
    margin-right: 6px;
    font-size: 12px;
  }
  &__label {
    flex: 1;
  }
  &__userid {
    color: #909399;
    font-size: 12px;
    margin-left: 8px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/wecom/components/department-tree.vue